import { UseAuthResult } from "core/auth/model";
import { StatusPage } from "UIPalette/StatusPage/StatusPage";
import { onLogout } from "core/javascriptChannels";
import { useTranslation } from "react-i18next";
import { useSSO } from "../../hooks/useSSO/useSSO";
import InfoAlert from "shared/UI/Alerts/InfoAlert";
import { trackClientCodeConflicts } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { useEffect } from "react";

interface AuthErrorProps {
  auth: UseAuthResult;
  status: 401 | 500;
  onRetry: () => void;
}

export const AuthError = ({ auth, status, onRetry }: AuthErrorProps) => {
  const { t } = useTranslation();
  const { signedUpWithSso, hasSetupSso } = useSSO();

  const is401Error = status === 401;
  const showSsoSignupError = is401Error && signedUpWithSso;
  const showSsoSetupError = is401Error && hasSetupSso && !signedUpWithSso;

  useEffect(() => {
    trackClientCodeConflicts(EventNames.SSO_CLIENT_CODE_CONFLICT_AUTH_ERROR, signedUpWithSso, hasSetupSso);
  }, [signedUpWithSso, hasSetupSso]);

  return (
    <StatusPage
      status="error"
      title={t(`auth.error.${status}.title`)}
      body={
        <div className="space-y-4">
          <p>{t(`auth.error.${status}.description`)}</p>
          {showSsoSignupError && (
            <InfoAlert title={t("auth.error.ssoSignUpInfo.title")}>
              <p className="text-start">{t("auth.error.ssoSignUpInfo.message")}</p>
            </InfoAlert>
          )}
          {showSsoSetupError && (
            <InfoAlert title={t("auth.error.ssoSignInInfo.title")}>
              <p className="text-start">{t("auth.error.ssoSignInInfo.message")}</p>
            </InfoAlert>
          )}
        </div>
      }
      cta={{
        id: "login-status-confirm-button",
        text: t(`auth.error.${status}.button`),
        onClick: () => {
          if (status === 401) {
            auth.signoutRedirect();
            onLogout();
          }
          if (status === 500) {
            onRetry();
          }
        },
      }}
    />
  );
};
